import { useState, useRef, useCallback, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, Sparkles, Loader2, Copy, Download, Check, FileAudio, X, Languages, ChevronsUpDown, Wand2, FileText, List, LayoutList, Newspaper, Mail, Share2, Subtitles, Undo2, Zap, Mic, Link2, Globe, User, LogOut, History, CreditCard, Activity, Layers } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Toaster, toast } from "sonner";
import { I18nProvider, useI18n } from "@/i18n";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "@/AuthContext";
import { lazy, Suspense } from "react";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const StudioPage = lazy(() => import("@/pages/StudioPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POPULAR_LANGUAGES = ["fr", "en", "ar", "es"];

const ALL_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "fr", name: "Francais" },
  { code: "es", name: "Espanol" },
  { code: "ar", name: "Arabe" },
  { code: "zh", name: "Chinois (simplifie)" },
  { code: "hi", name: "Hindi" },
  { code: "pt", name: "Portugais" },
  { code: "ru", name: "Russe" },
  { code: "ja", name: "Japonais" },
  { code: "de", name: "Allemand" },
  { code: "ko", name: "Coreen" },
  { code: "it", name: "Italien" },
  { code: "tr", name: "Turc" },
  { code: "nl", name: "Neerlandais" },
  { code: "pl", name: "Polonais" },
  { code: "id", name: "Indonesien" },
  { code: "vi", name: "Vietnamien" },
  { code: "th", name: "Thai" },
  { code: "fa", name: "Persan (Farsi)" },
  { code: "ur", name: "Ourdou" },
  { code: "ms", name: "Malais" },
  { code: "sw", name: "Swahili" },
  { code: "bn", name: "Bengali" },
  { code: "ta", name: "Tamoul" },
  { code: "te", name: "Telugu" },
  { code: "mr", name: "Marathi" },
  { code: "pa", name: "Pendjabi" },
  { code: "ro", name: "Roumain" },
  { code: "el", name: "Grec" },
  { code: "cs", name: "Tcheque" },
  { code: "mt", name: "Maltais" },
  { code: "he", name: "Hebreu" },
];

const POPULAR_LIST = ALL_LANGUAGES.filter((l) => POPULAR_LANGUAGES.includes(l.code));
const OTHER_LIST = ALL_LANGUAGES.filter((l) => !POPULAR_LANGUAGES.includes(l.code));

const SMART_ACTIONS = [
  { id: "summary_short", name: "Résumé", icon: FileText, category: "smart" },
  { id: "key_points", name: "Points clés", icon: List, category: "smart" },
  { id: "structured_plan", name: "Plan structuré", icon: LayoutList, category: "smart" },
];

const CONTENT_ACTIONS = [
  { id: "article", name: "Article", icon: Newspaper, category: "content" },
  { id: "email", name: "Email", icon: Mail, category: "content" },
  { id: "linkedin_post", name: "Post LinkedIn", icon: Share2, category: "content" },
];

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

const LOGO_URL = "https://customer-assets.emergentagent.com/job_translate-vocal/artifacts/a3ccudo2_Capture_d_e%CC%81cran_2026-04-15_a%CC%80_22.49.06-removebg-preview.png";

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="ambient-bg" />
      <Loader2 className="w-8 h-8 text-[#6C5CE7] animate-spin" />
    </div>
  );
}

function Header({ onLogoClick }) {
  const { t, locale, changeLocale, SITE_LANGUAGES } = useI18n();
  const { user, logout, token } = useAuth();
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const currentLang = SITE_LANGUAGES.find(l => l.code === locale);

  useEffect(() => {
    if (!user || !token) { setIsAdmin(false); return; }
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      .then(r => setIsAdmin(!!r.data.is_admin))
      .catch(() => setIsAdmin(false));
  }, [user, token]);

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto flex items-center justify-between h-16">
        <div className="logo-glow flex items-center cursor-pointer" data-testid="app-logo" onClick={onLogoClick}>
          <img src={LOGO_URL} alt="VocalTranslate" className="h-11 sm:h-12 w-auto" />
        </div>
        <div className="flex items-center gap-3">
          <Link to="/pricing" data-testid="nav-pricing" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[#A1A1AA] hover:text-white hover:bg-white/10 transition-colors">
            <CreditCard className="w-3.5 h-3.5" />
            Plans
          </Link>
          {user && (
            <Link to="/studio" data-testid="nav-studio" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-gradient-to-r from-[#6C5CE7]/20 to-[#8A7BFF]/20 text-[#8A7BFF] hover:text-white hover:from-[#6C5CE7]/30 hover:to-[#8A7BFF]/30 transition-colors">
              <Layers className="w-3.5 h-3.5" />
              Studio
            </Link>
          )}
          {user && (
            <Link to="/history" data-testid="nav-history" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[#A1A1AA] hover:text-white hover:bg-white/10 transition-colors">
              <History className="w-3.5 h-3.5" />
              Historique
            </Link>
          )}
          <Popover open={langOpen} onOpenChange={setLangOpen}>
            <PopoverTrigger asChild>
              <button data-testid="site-language-switcher" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-[#A1A1AA] hover:text-white hover:bg-white/10 transition-colors">
                <Globe className="w-3.5 h-3.5" />
                {currentLang?.name}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[160px] p-1 bg-[#1a1a1d] border-white/10" align="end">
              {SITE_LANGUAGES.map((lang) => (
                <button key={lang.code} data-testid={`site-lang-${lang.code}`}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${locale === lang.code ? "bg-[#6C5CE7]/20 text-[#6C5CE7]" : "text-white hover:bg-white/10"}`}
                  onClick={() => { changeLocale(lang.code); setLangOpen(false); }}>
                  {lang.name}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          {user ? (
            <Popover open={userOpen} onOpenChange={setUserOpen}>
              <PopoverTrigger asChild>
                <button data-testid="user-menu-button" className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#6C5CE7]/10 border border-[#6C5CE7]/20 text-xs text-[#6C5CE7] hover:bg-[#6C5CE7]/20 transition-colors">
                  <User className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline max-w-[100px] truncate">{user.name || user.email?.split("@")[0]}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-1 bg-[#1a1a1d] border-white/10" align="end">
                <div className="px-3 py-2 border-b border-white/5 mb-1">
                  <p className="text-xs text-white font-medium truncate">{user.email}</p>
                  <p className="text-[10px] text-[#6C5CE7] uppercase" data-testid="user-menu-plan">{({ free: "Gratuit", starter: "Starter", pro: "Pro", business: "Business", unlimited: "Illimite" })[user.plan] || "Gratuit"}</p>
                  <p className="text-[10px] text-[#A1A1AA] mt-0.5" data-testid="user-menu-minutes">
                    {user.minutes_remaining === null ? "Minutes illimitees" : `${user.minutes_remaining ?? 0} min restantes`}
                  </p>
                </div>
                <Link to="/pricing" className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md" onClick={() => setUserOpen(false)}>
                  <CreditCard className="w-3.5 h-3.5" /> Plans
                </Link>
                <Link to="/studio" data-testid="user-menu-studio" className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#8A7BFF] hover:bg-[#6C5CE7]/10 rounded-md" onClick={() => setUserOpen(false)}>
                  <Layers className="w-3.5 h-3.5" /> Studio
                </Link>
                <Link to="/history" className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md" onClick={() => setUserOpen(false)}>
                  <History className="w-3.5 h-3.5" /> Historique
                </Link>
                {isAdmin && (
                  <Link to="/admin" data-testid="admin-link" className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#8A7BFF] hover:bg-[#6C5CE7]/10 rounded-md" onClick={() => setUserOpen(false)}>
                    <Activity className="w-3.5 h-3.5" /> Admin
                  </Link>
                )}
                <button data-testid="logout-button" onClick={() => { logout(); setUserOpen(false); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-md">
                  <LogOut className="w-3.5 h-3.5" /> Déconnexion
                </button>
              </PopoverContent>
            </Popover>
          ) : (
            <Link to="/login" data-testid="login-button"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#6C5CE7] text-white text-xs font-medium hover:bg-[#5A4BC4] transition-colors">
              <User className="w-3.5 h-3.5" />
              Connexion
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function UploadZone({ file, setFile, isDragOver, setIsDragOver, urlInput, setUrlInput, inputMode, setInputMode }) {
  const inputRef = useRef(null);
  const { t } = useI18n();

  const validateFile = useCallback((f) => {
    if (f.size > 50 * 1024 * 1024) {
      toast.error(t("toastTooBig"));
      return false;
    }
    return true;
  }, [t]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
      setInputMode("file");
    }
  }, [setFile, setIsDragOver, validateFile, setInputMode]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragOver(true); }, [setIsDragOver]);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragOver(false); }, [setIsDragOver]);

  const handleFileSelect = useCallback((e) => {
    const selected = e.target.files[0];
    if (selected && validateFile(selected)) {
      setFile(selected);
      setInputMode("file");
    }
  }, [setFile, validateFile, setInputMode]);

  return (
    <div data-testid="upload-dropzone">
      {/* Mode tabs */}
      <div className="flex gap-2 mb-4">
        <button
          data-testid="tab-file"
          className={`action-btn text-sm ${inputMode === "file" ? "copied" : ""}`}
          onClick={() => setInputMode("file")}
        >
          <UploadCloud className="w-4 h-4" />
          {t("tabFile")}
        </button>
        <button
          data-testid="tab-link"
          className={`action-btn text-sm ${inputMode === "link" ? "copied" : ""}`}
          onClick={() => setInputMode("link")}
        >
          <Link2 className="w-4 h-4" />
          {t("tabLink")}
        </button>
      </div>

      {inputMode === "file" ? (
        <div
          className={`upload-zone relative flex flex-col items-center justify-center p-10 sm:p-14 rounded-3xl cursor-pointer group ${isDragOver ? "drag-over" : ""} ${file ? "has-file" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !file && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept="audio/*,video/*,.mp3,.wav,.m4a,.mp4,.webm,.flac,.ogg,.aac,.aiff,.wma,.opus,.3gp"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="file-input"
          />
          {!file ? (
            <>
              <UploadCloud className="w-10 h-10 text-[#A1A1AA] group-hover:text-[#6C5CE7] transition-colors mb-4" />
              <p className="text-base font-medium text-white mb-2">{t("dropText")}</p>
              <p className="text-sm text-[#71717A]">{t("dropFormats")}</p>
            </>
          ) : (
            <div className="flex items-center justify-between w-full">
              <div className="file-chip flex-1">
                <FileAudio className="w-5 h-5 text-[#6C5CE7] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">{file.name}</p>
                  <p className="text-xs text-[#71717A]">{formatFileSize(file.size)}</p>
                </div>
              </div>
              <button data-testid="remove-file-button" className="ml-4 p-2 rounded-lg hover:bg-white/10 transition-colors" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                <X className="w-4 h-4 text-[#A1A1AA]" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="surface-card p-6 sm:p-8 rounded-3xl">
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-5 h-5 text-[#6C5CE7]" />
            <p className="text-base font-medium text-white">{t("orPasteLink")}</p>
          </div>
          <input
            data-testid="url-input"
            type="url"
            className="w-full h-12 px-4 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-[#71717A] focus:border-[#6C5CE7] focus:outline-none transition-colors"
            placeholder={t("linkPlaceholder")}
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
          />
          <p className="text-xs text-[#71717A] mt-2">Liens directs vers fichiers audio/video (.mp3, .wav, .m4a...)</p>
        </div>
      )}
    </div>
  );
}

function LanguageSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = ALL_LANGUAGES.find((l) => l.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="target-language-select"
          className="flex items-center justify-between w-full sm:w-[240px] h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white hover:bg-white/8 transition-colors"
        >
          <span>{selected ? selected.name : "Choisir une langue"}</span>
          <ChevronsUpDown className="w-4 h-4 text-[#71717A] ml-2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0 bg-[#1a1a1d] border-white/10"
        align="start"
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder="Rechercher une langue..."
            className="text-white placeholder:text-[#71717A]"
            data-testid="language-search-input"
          />
          <CommandList>
            <CommandEmpty className="text-[#71717A]">Aucune langue trouvée.</CommandEmpty>
            <CommandGroup heading="Langues populaires" className="text-[#71717A]">
              {POPULAR_LIST.map((lang) => (
                <CommandItem
                  key={lang.code}
                  value={`${lang.name} ${lang.code}`}
                  keywords={[lang.name, lang.code]}
                  onSelect={() => { onChange(lang.code); setOpen(false); }}
                  className="text-white cursor-pointer data-[selected=true]:bg-[#6C5CE7]/20 data-[selected=true]:text-white"
                  data-testid={`lang-option-${lang.code}`}
                >
                  <span className="flex-1">{lang.name}</span>
                  {value === lang.code && <Check className="w-4 h-4 text-[#6C5CE7]" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator className="bg-white/10" />
            <CommandGroup heading="Toutes les langues" className="text-[#71717A]">
              {OTHER_LIST.map((lang) => (
                <CommandItem
                  key={lang.code}
                  value={`${lang.name} ${lang.code}`}
                  keywords={[lang.name, lang.code]}
                  onSelect={() => { onChange(lang.code); setOpen(false); }}
                  className="text-white cursor-pointer data-[selected=true]:bg-[#6C5CE7]/20 data-[selected=true]:text-white"
                  data-testid={`lang-option-${lang.code}`}
                >
                  <span className="flex-1">{lang.name}</span>
                  {value === lang.code && <Check className="w-4 h-4 text-[#6C5CE7]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SourceLanguageSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const selected = value === "auto" ? null : ALL_LANGUAGES.find((l) => l.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="source-language-select"
          className="flex items-center justify-between w-full sm:w-[240px] h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white hover:bg-white/8 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Mic className="w-3.5 h-3.5 text-[#71717A]" />
            {selected ? selected.name : t("autoDetect")}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-[#71717A] ml-2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0 bg-[#1a1a1d] border-white/10" align="start">
        <Command className="bg-transparent">
          <CommandInput placeholder="Rechercher..." className="text-white placeholder:text-[#71717A]" />
          <CommandList>
            <CommandEmpty className="text-[#71717A]">Aucune langue trouvée.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="auto detection automatique"
                onSelect={() => { onChange("auto"); setOpen(false); }}
                className="text-white cursor-pointer data-[selected=true]:bg-[#6C5CE7]/20"
                data-testid="source-lang-auto"
              >
                <span className="flex-1">{t("autoDetect")}</span>
                {value === "auto" && <Check className="w-4 h-4 text-[#6C5CE7]" />}
              </CommandItem>
            </CommandGroup>
            <CommandSeparator className="bg-white/10" />
            <CommandGroup heading="Langues populaires" className="text-[#71717A]">
              {POPULAR_LIST.map((lang) => (
                <CommandItem
                  key={lang.code}
                  value={`${lang.name} ${lang.code}`}
                  keywords={[lang.name, lang.code]}
                  onSelect={() => { onChange(lang.code); setOpen(false); }}
                  className="text-white cursor-pointer data-[selected=true]:bg-[#6C5CE7]/20"
                >
                  <span className="flex-1">{lang.name}</span>
                  {value === lang.code && <Check className="w-4 h-4 text-[#6C5CE7]" />}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator className="bg-white/10" />
            <CommandGroup heading="Toutes les langues" className="text-[#71717A]">
              {OTHER_LIST.map((lang) => (
                <CommandItem
                  key={lang.code}
                  value={`${lang.name} ${lang.code}`}
                  keywords={[lang.name, lang.code]}
                  onSelect={() => { onChange(lang.code); setOpen(false); }}
                  className="text-white cursor-pointer data-[selected=true]:bg-[#6C5CE7]/20"
                >
                  <span className="flex-1">{lang.name}</span>
                  {value === lang.code && <Check className="w-4 h-4 text-[#6C5CE7]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ControlsRow({ sourceLanguage, setSourceLanguage, targetLanguage, setTargetLanguage, generateSummary, setGenerateSummary }) {
  const { t } = useI18n();
  return (
    <div className="surface-card p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
      <div className="flex flex-col gap-2 w-full sm:w-auto">
        <label className="text-sm font-medium text-[#A1A1AA]">{t("sourceLang")}</label>
        <SourceLanguageSelector value={sourceLanguage} onChange={setSourceLanguage} />
      </div>

      <div className="flex flex-col gap-2 w-full sm:w-auto">
        <label className="text-sm font-medium text-[#A1A1AA]">{t("targetLang")}</label>
        <LanguageSelector value={targetLanguage} onChange={setTargetLanguage} />
      </div>

      <div className="flex items-center gap-3 sm:ml-auto sm:mt-6">
        <label htmlFor="summary-switch" className="text-sm font-medium text-[#A1A1AA] cursor-pointer">
          {t("summary")}
        </label>
        <Switch
          id="summary-switch"
          data-testid="summary-switch"
          checked={generateSummary}
          onCheckedChange={setGenerateSummary}
          className="data-[state=checked]:bg-[#6C5CE7]"
        />
      </div>
    </div>
  );
}

function LoadingState({ step }) {
  const steps = {
    pending: "Preparation...",
    upload: "Preparation...",
    transcription: "Transcription en cours...",
    translation: "Traduction en cours...",
    summary: "Generation du resume...",
    downloading: "Telechargement de l'audio...",
    converting: "Conversion du format...",
    compressing: "Compression du fichier...",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-16"
      data-testid="loading-state"
    >
      <div className="relative mb-6 w-16 h-16">
        <div className="w-16 h-16 rounded-full bg-[#6C5CE7]/10 flex items-center justify-center pulse-glow">
          <Loader2 className="w-8 h-8 text-[#6C5CE7] animate-spin-slow" />
        </div>
        <div className="orbit-dot" />
        <div className="orbit-dot" />
        <div className="orbit-dot" />
      </div>
      <p className="text-lg font-medium text-white mb-2">Analyse en cours...</p>
      <p className="text-sm text-[#71717A]">{steps[step] || "Traitement..."}</p>
    </motion.div>
  );
}

function ResultBlock({ title, text, languageCode, languageName, testIdPrefix, onImprove, isImproving, isImproved, onRevert, showSrt, onDownloadSrt }) {
  const [copied, setCopied] = useState(false);
  const displayText = text || "";

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(displayText);
    setCopied(true);
    toast.success("Texte copié !");
    setTimeout(() => setCopied(false), 2000);
  }, [displayText]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([displayText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.toLowerCase()}_${languageCode}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Fichier téléchargé !");
  }, [displayText, title, languageCode]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="surface-card p-6 flex flex-col h-full"
      data-testid={`${testIdPrefix}-result`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {isImproved && (
            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 rounded-lg text-[10px]">
              Amélioré
            </Badge>
          )}
        </div>
        <Badge className="bg-[#6C5CE7]/15 text-[#6C5CE7] border-[#6C5CE7]/30 rounded-lg text-xs font-mono">
          {languageName || languageCode?.toUpperCase()}
        </Badge>
      </div>

      <div className="result-text flex-1 mb-4 min-h-[120px]">
        {displayText}
      </div>

      <div className="flex items-center gap-2 justify-between pt-3 border-t border-white/5">
        <div className="flex items-center gap-2">
          {onImprove && (
            <button
              data-testid={`${testIdPrefix}-improve-button`}
              className={`action-btn ${isImproved ? "copied" : ""}`}
              onClick={onImprove}
              disabled={isImproving}
            >
              {isImproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {isImproving ? "..." : "Améliorer"}
            </button>
          )}
          {isImproved && onRevert && (
            <button
              data-testid={`${testIdPrefix}-revert-button`}
              className="action-btn"
              onClick={onRevert}
            >
              <Undo2 className="w-4 h-4" />
              Original
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showSrt && onDownloadSrt && (
            <button
              data-testid={`${testIdPrefix}-srt-button`}
              className="action-btn"
              onClick={onDownloadSrt}
            >
              <Subtitles className="w-4 h-4" />
              SRT
            </button>
          )}
          <button
            data-testid={`${testIdPrefix}-copy-button`}
            className={`action-btn ${copied ? "copied" : ""}`}
            onClick={handleCopy}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copié" : "Copier"}
          </button>
          <button
            data-testid={`${testIdPrefix}-download-button`}
            className="action-btn"
            onClick={handleDownload}
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function SummaryBlock({ summary }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(summary);
    setCopied(true);
    toast.success("Résumé copié !");
    setTimeout(() => setCopied(false), 2000);
  }, [summary]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="surface-card p-6"
      data-testid="summary-result"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Resume</h3>
        <button
          data-testid="summary-copy-button"
          className={`action-btn ${copied ? "copied" : ""}`}
          onClick={handleCopy}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copie" : "Copier"}
        </button>
      </div>
      <p className="text-[15px] leading-relaxed text-white/80">{summary}</p>
    </motion.div>
  );
}

function ActionBar({ onGenerate, onGenerateAll, activeMode, isGenerating }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="space-y-4"
      data-testid="action-bar"
    >
      {/* Primary CTA — Tout générer */}
      <div className="surface-card p-6 text-center">
        <p className="text-sm text-[#A1A1AA] mb-4">Générez tout votre contenu en <span className="text-white font-medium">1 clic</span></p>
        <button
          data-testid="generate-all-button"
          className="cta-button cta-glow-pulse mx-auto text-base py-4 px-10"
          onClick={onGenerateAll}
          disabled={!!isGenerating}
        >
          {isGenerating === "all" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
          Tout générer — Article, Email, Post
        </button>
        <p className="text-[11px] text-[#6C5CE7] mt-3">1 audio → 5 contenus générés instantanément</p>
      </div>

      {/* Secondary actions */}
      <div className="surface-card p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Résultat intelligent */}
          <div>
            <p className="text-xs font-medium text-[#71717A] uppercase tracking-wider mb-3">Analyser</p>
            <div className="flex flex-wrap gap-2">
              {SMART_ACTIONS.map((action) => {
                const Icon = action.icon;
                const isActive = activeMode === action.id;
                const isLoading = isGenerating === action.id;
                return (
                  <button
                    key={action.id}
                    data-testid={`action-${action.id}`}
                    className={`action-btn ${isActive ? "copied" : ""}`}
                    onClick={() => onGenerate(action.id)}
                    disabled={!!isGenerating}
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                    {action.name}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Générer du contenu individuellement */}
          <div>
            <p className="text-xs font-medium text-[#71717A] uppercase tracking-wider mb-3">Générer individuellement</p>
            <div className="flex flex-wrap gap-2">
              {CONTENT_ACTIONS.map((action) => {
                const Icon = action.icon;
                const isActive = activeMode === action.id;
                const isLoading = isGenerating === action.id;
                return (
                  <button
                    key={action.id}
                    data-testid={`action-${action.id}`}
                    className={`action-btn ${isActive ? "copied" : ""}`}
                    onClick={() => onGenerate(action.id)}
                    disabled={!!isGenerating}
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                    {action.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const READY_TITLES = {
  "Article": "Article prêt à publier",
  "Email professionnel": "Email prêt à envoyer",
  "Post LinkedIn": "Post prêt à poster",
};

function GeneratedContentCard({ content, templateName, onClose, index }) {
  const [copied, setCopied] = useState(false);
  const readyTitle = READY_TITLES[templateName] || templateName;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success("Contenu copié !");
    setTimeout(() => setCopied(false), 2000);
  }, [content]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${templateName.toLowerCase().replace(/\s+/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Fichier téléchargé !");
  }, [content, templateName]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: (index || 0) * 0.15, ease: [0.16, 1, 0.3, 1] }}
      className="surface-card p-6"
      data-testid="generated-content-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-white">{readyTitle}</h3>
          <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 rounded-lg text-[10px]">
            Prêt
          </Badge>
        </div>
        <button className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" onClick={onClose}>
          <X className="w-4 h-4 text-[#A1A1AA]" />
        </button>
      </div>
      <div className="result-text mb-4 min-h-[80px] whitespace-pre-wrap">
        {content}
      </div>
      <div className="flex items-center gap-2 justify-end pt-3 border-t border-white/5">
        <button
          data-testid="generated-copy-button"
          className={`action-btn ${copied ? "copied" : ""}`}
          onClick={handleCopy}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copie" : "Copier"}
        </button>
        <button data-testid="generated-download-button" className="action-btn" onClick={handleDownload}>
          <Download className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <I18nProvider>
          <AppRouter />
        </I18nProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRouter() {
  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: "#1a1a1d", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" },
        }}
      />
      <Routes>
        <Route path="/login" element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
        <Route path="/auth/callback" element={<Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>} />
        <Route path="/pricing" element={<Suspense fallback={<PageLoader />}><PricingPage /></Suspense>} />
        <Route path="/history" element={<Suspense fallback={<PageLoader />}><HistoryPage /></Suspense>} />
        <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminPage /></Suspense>} />
        <Route path="/studio" element={<Suspense fallback={<PageLoader />}><StudioPage /></Suspense>} />
        <Route path="*" element={<HashCallbackOrMain />} />
      </Routes>
    </>
  );
}

function HashCallbackOrMain() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <MainApp />;
}

function MainApp() {
  const navigate = useNavigate();
  const { token, user, checkAuth } = useAuth();
  const [file, setFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState("fr");
  const [sourceLanguage, setSourceLanguage] = useState("auto");
  const [generateSummary, setGenerateSummary] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState("");
  const [result, setResult] = useState(null);
  const [inputMode, setInputMode] = useState("file");
  const [urlInput, setUrlInput] = useState("");
  const pollRef = useRef(null);
  const { t, isRtl } = useI18n();

  // Smart features state
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatedTemplateName, setGeneratedTemplateName] = useState("");
  const [allContents, setAllContents] = useState(null);
  const [generatingMode, setGeneratingMode] = useState(null);
  const [activeMode, setActiveMode] = useState(null);
  const [improvedTranscription, setImprovedTranscription] = useState(null);
  const [improvedTranslation, setImprovedTranslation] = useState(null);
  const [improvingField, setImprovingField] = useState(null);

  const pollJob = useCallback(async (jobId) => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`);
      const job = response.data;

      setCurrentStep(job.step || "processing");

      if (job.status === "completed") {
        clearInterval(pollRef.current);
        setIsProcessing(false);
        setResult(job);
        toast.success("Traitement termine !");
        checkAuth();
      } else if (job.status === "error") {
        clearInterval(pollRef.current);
        setIsProcessing(false);
        toast.error(job.error || "Une erreur est survenue");
        checkAuth();
      }
    } catch (err) {
      console.error("Polling error:", err);
    }
  }, [checkAuth]);

  const showSubmitError = useCallback((err, fallback) => {
    setIsProcessing(false);
    const msg = err.response?.data?.detail || fallback;
    if (err.response?.status === 402) {
      toast.error(msg, { action: { label: "Voir les forfaits", onClick: () => navigate("/pricing") } });
    } else {
      toast.error(msg);
    }
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    if (!user) {
      toast.error("Connectez-vous pour lancer une transcription");
      navigate("/login");
      return;
    }
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    if (inputMode === "link") {
      if (!urlInput.trim() || !urlInput.startsWith("http")) {
        toast.error("Veuillez entrer un lien valide");
        return;
      }
      setIsProcessing(true);
      setCurrentStep("downloading");
      setResult(null);
      try {
        const response = await axios.post(`${API}/process-url`, {
          url: urlInput.trim(),
          target_language: targetLanguage,
          source_language: sourceLanguage,
          generate_summary: generateSummary,
        }, { headers: authHeaders, withCredentials: true });
        const { job_id } = response.data;
        pollRef.current = setInterval(() => pollJob(job_id), 1000);
      } catch (err) {
        showSubmitError(err, "Erreur lors du traitement du lien");
      }
      return;
    }

    if (!file) {
        toast.error("Veuillez sélectionner un fichier audio");
      return;
    }

    setIsProcessing(true);
    setCurrentStep("upload");
    setResult(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("target_language", targetLanguage);
    formData.append("source_language", sourceLanguage);
    formData.append("generate_summary", generateSummary);

    try {
      const response = await axios.post(`${API}/process-audio`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...authHeaders,
        },
        withCredentials: true,
      });

      const { job_id } = response.data;

      // Poll for results
      pollRef.current = setInterval(() => pollJob(job_id), 1000);
    } catch (err) {
      showSubmitError(err, "Erreur lors du traitement");
    }
  }, [file, targetLanguage, sourceLanguage, generateSummary, pollJob, inputMode, urlInput, user, token, navigate, showSubmitError]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleReset = useCallback(() => {
    setFile(null);
    setResult(null);
    setIsProcessing(false);
    setCurrentStep("");
    setGeneratedContent(null);
    setGeneratedTemplateName("");
    setAllContents(null);
    setGeneratingMode(null);
    setActiveMode(null);
    setImprovedTranscription(null);
    setImprovedTranslation(null);
    setImprovingField(null);
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const handleGenerateContent = useCallback(async (templateId) => {
    if (!result?.translation) return;
    setGeneratingMode(templateId);
    try {
      // Use translation text (already in target language) for content generation
      const resp = await axios.post(`${API}/generate-content`, {
        text: result.translation,
        template_id: templateId,
        language: result.target_language || targetLanguage,
      });
      setGeneratedContent(resp.data.content);
      setGeneratedTemplateName(resp.data.template_name);
      setActiveMode(templateId);
      toast.success("Contenu généré !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de génération");
    } finally {
      setGeneratingMode(null);
    }
  }, [result, targetLanguage]);

  const handleGenerateAll = useCallback(async () => {
    if (!result?.translation) return;
    setGeneratingMode("all");
    setAllContents(null);
    setGeneratedContent(null);
    const lang = result.target_language || targetLanguage;
    const text = result.translation;
    try {
      const [article, email, post] = await Promise.all([
        axios.post(`${API}/generate-content`, { text, template_id: "article", language: lang }),
        axios.post(`${API}/generate-content`, { text, template_id: "email", language: lang }),
        axios.post(`${API}/generate-content`, { text, template_id: "linkedin_post", language: lang }),
      ]);
      setAllContents([
        { name: article.data.template_name, content: article.data.content, id: "article" },
        { name: email.data.template_name, content: email.data.content, id: "email" },
        { name: post.data.template_name, content: post.data.content, id: "linkedin_post" },
      ]);
      setActiveMode("all");
      toast.success("Tous les contenus générés !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de génération");
    } finally {
      setGeneratingMode(null);
    }
  }, [result, targetLanguage]);

  const handleImproveText = useCallback(async (field) => {
    if (!result) return;
    const text = field === "transcription" ? result.transcription : result.translation;
    const lang = field === "transcription" ? result.source_language : (result.target_language || targetLanguage);
    setImprovingField(field);
    try {
      const resp = await axios.post(`${API}/improve-text`, { text, language: lang });
      if (field === "transcription") {
        setImprovedTranscription(resp.data.improved_text);
      } else {
        setImprovedTranslation(resp.data.improved_text);
      }
      toast.success("Texte amélioré !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'amélioration");
    } finally {
      setImprovingField(null);
    }
  }, [result, targetLanguage]);

  const handleDownloadSrt = useCallback(async (translated = false) => {
    if (!result?.id) return;
    try {
      const resp = await axios.get(`${API}/jobs/${result.id}/srt`, {
        params: { translated },
        responseType: "text",
      });
      const blob = new Blob([resp.data], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `subtitles_${translated ? result.target_language : result.source_language}.srt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Sous-titres SRT téléchargés !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur SRT");
    }
  }, [result]);

  return (
    <div className={`min-h-screen relative ${isRtl ? "rtl" : ""}`}>
      <div className="ambient-bg" />

      <Header onLogoClick={handleReset} />

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4"
            data-testid="hero-title"
          >
            {t("heroTitle1")}{" "}
            <span className="text-[#6C5CE7]">{t("heroTitle2")}</span>
          </h1>
          <p className="text-base sm:text-lg text-[#A1A1AA] max-w-2xl mx-auto leading-relaxed">
            {t("heroDesc")}
          </p>
        </motion.div>

        {/* SEO text block — visible only on landing (no result) */}
        {!result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="max-w-3xl mx-auto mb-10 text-center space-y-3"
          >
            <p className="text-sm text-[#71717A] leading-relaxed">
              VocalTranslate est un outil d'intelligence artificielle qui permet de transformer automatiquement un audio en texte, traduction et contenu exploitable. Compatible avec de nombreux formats audio et vidéo (MP3, WAV, MP4, FLAC, AAC, OGG...), VocalTranslate s'adapte à tous les usages.
            </p>
            <p className="text-xs text-[#52525B]">
              Idéal pour les créateurs de contenu, journalistes, entrepreneurs et équipes internationales.
            </p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="space-y-6"
            >
              {/* Upload Zone */}
              <UploadZone
                file={file}
                setFile={setFile}
                isDragOver={isDragOver}
                setIsDragOver={setIsDragOver}
                urlInput={urlInput}
                setUrlInput={setUrlInput}
                inputMode={inputMode}
                setInputMode={setInputMode}
              />

              {/* Controls */}
              <ControlsRow
                sourceLanguage={sourceLanguage}
                setSourceLanguage={setSourceLanguage}
                targetLanguage={targetLanguage}
                setTargetLanguage={setTargetLanguage}
                generateSummary={generateSummary}
                setGenerateSummary={setGenerateSummary}
              />

              {/* CTA or Loading */}
              {!isProcessing ? (
                <div className="flex justify-center pt-2">
                  <button
                    data-testid="transcribe-translate-button"
                    className="cta-button"
                    disabled={inputMode === "file" ? !file : !urlInput.trim()}
                    onClick={handleSubmit}
                  >
                    <Sparkles className="w-5 h-5" />
                    {t("ctaButton")}
                  </button>
                </div>
              ) : (
                <LoadingState step={currentStep} />
              )}
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="space-y-6"
            >
              {/* Detected Language + Speed indicator */}
              {result.source_language_name && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center gap-1"
                  data-testid="detected-language-info"
                >
                  <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <Languages className="w-4 h-4 text-[#6C5CE7]" />
                    <span>Langue détectée : <span className="text-white font-medium">{result.source_language_name}</span></span>
                  </div>
                  <p className="text-[11px] text-[#6C5CE7]">Traitement terminé — votre contenu est prêt</p>
                </motion.div>
              )}

              {/* Results Grid — Translation first (plus de valeur) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="results-grid">
                <ResultBlock
                  title="Traduction"
                  text={improvedTranslation || result.translation}
                  languageCode={result.target_language}
                  languageName={result.target_language_name}
                  testIdPrefix="translation"
                  onImprove={() => handleImproveText("translation")}
                  isImproving={improvingField === "translation"}
                  isImproved={!!improvedTranslation}
                  onRevert={() => setImprovedTranslation(null)}
                  showSrt={result.segments?.length > 0}
                  onDownloadSrt={() => handleDownloadSrt(true)}
                />
                <ResultBlock
                  title="Transcription"
                  text={improvedTranscription || result.transcription}
                  languageCode={result.source_language}
                  languageName={result.source_language_name}
                  testIdPrefix="transcription"
                  onImprove={() => handleImproveText("transcription")}
                  isImproving={improvingField === "transcription"}
                  isImproved={!!improvedTranscription}
                  onRevert={() => setImprovedTranscription(null)}
                  showSrt={result.segments?.length > 0}
                  onDownloadSrt={() => handleDownloadSrt(false)}
                />
              </div>

              {/* Summary */}
              {result.summary && <SummaryBlock summary={result.summary} />}

              {/* Smart Actions Bar */}
              <ActionBar
                onGenerate={(id) => { setAllContents(null); handleGenerateContent(id); }}
                onGenerateAll={handleGenerateAll}
                activeMode={activeMode}
                isGenerating={generatingMode}
              />

              {/* Single Generated Content */}
              <AnimatePresence>
                {generatedContent && !allContents && (
                  <GeneratedContentCard
                    content={generatedContent}
                    templateName={generatedTemplateName}
                    index={0}
                    onClose={() => { setGeneratedContent(null); setActiveMode(null); }}
                  />
                )}
              </AnimatePresence>

              {/* All Generated Contents */}
              <AnimatePresence>
                {allContents && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-4"
                    data-testid="all-contents"
                  >
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center text-sm text-[#6C5CE7] font-medium"
                    >
                      {allContents.length} contenus générés — prêts à utiliser
                    </motion.p>
                    {allContents.map((item, i) => (
                      <GeneratedContentCard
                        key={item.id}
                        content={item.content}
                        templateName={item.name}
                        index={i}
                        onClose={() => {
                          setAllContents((prev) => prev.filter((_, j) => j !== i));
                          if (allContents.length <= 1) setActiveMode(null);
                        }}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* New translation button */}
              <div className="flex justify-center pt-4">
                <button
                  data-testid="new-translation-button"
                  className="action-btn text-base px-6 py-3"
                  onClick={handleReset}
                >
                  <UploadCloud className="w-5 h-5" />
                  {t("newTranslation")}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <footer className="relative z-10 pb-6 pt-12 text-center">
        <p className="text-xs text-[#71717A]">&copy; {new Date().getFullYear()} VocalTranslate.ai — Développée et créée par Kourak Group</p>
      </footer>
    </div>
  );
}
